/**
 * Splits `value` on the last occurrence of `delimiter`, returning both the
 * untouched original string and its trailing segment as a display label —
 * e.g. shortening a hyphen-delimited batch ID for a table cell while still
 * exposing the full value for a tooltip. When `delimiter` is a list, the
 * split happens at whichever candidate delimiter occurs last anywhere in the
 * string — robust for IDs whose separator varies (`-`, `_`, or `/`). Falls
 * back to `value` for `displayLabel` when no delimiter is present.
 * @example splitDisplayLabel('adidas-us-catalog-ops-20260706150359229', '-')
 *   => { original: 'adidas-us-catalog-ops-20260706150359229', displayLabel: '20260706150359229' }
 * @example splitDisplayLabel('brand/style_SKU-4821', ['-', '_', '/'])
 *   => { original: 'brand/style_SKU-4821', displayLabel: '4821' }
 * @example splitDisplayLabel('no-delimiter-here', '/')
 *   => { original: 'no-delimiter-here', displayLabel: 'no-delimiter-here' }
 */
export function splitDisplayLabel(
  value: string,
  delimiter: string | readonly string[],
): { original: string; displayLabel: string } {
  const candidates = typeof delimiter === 'string' ? [delimiter] : delimiter;
  // Pick the delimiter whose last occurrence sits furthest right, so the
  // display label is always the final trailing segment regardless of which
  // separator the ID happens to use.
  let splitIndex = -1;
  let matchedLength = 0;
  for (const candidate of candidates) {
    if (!candidate) continue;
    const lastIndex = value.lastIndexOf(candidate);
    if (lastIndex > splitIndex) {
      splitIndex = lastIndex;
      matchedLength = candidate.length;
    }
  }
  if (splitIndex === -1) {
    return { original: value, displayLabel: value };
  }
  return { original: value, displayLabel: value.slice(splitIndex + matchedLength) };
}
